import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaEye, FaTimesCircle, FaSearch, FaFilter } from "react-icons/fa";
import { useVerificationRequests, useVerifyInfluencer } from "../../hooks/useAdmin.ts";
import { Link } from "react-router-dom";
import Pagination from "../../components/Pagination.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

interface InfluencerVerificationRequest {
  id: string;
  profile: string;
  requested_at: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  influencer_name: string;
  influencer_profile_id: string;
  influencer_id: number;
}

const VerificationRequestInfluencer: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [requests, setRequests] = useState<InfluencerVerificationRequest[]>([]);
  const [popup, setPopup] = useState<{ type: "approve" | "reject"; request: InfluencerVerificationRequest } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "PENDING" | "APPROVED" | "REJECTED">("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const { data: verificationsData } = useVerificationRequests();
  const verifyInfluencerMutation = useVerifyInfluencer();

  useEffect(() => {
    if (verificationsData) {
      // Filter out organization requests and keep only influencer requests
      // Handle both direct array and nested data structure
      const allRequests = Array.isArray(verificationsData)
        ? verificationsData
        : (verificationsData as any)?.data || [];

      const influencerRequests = allRequests.filter(
        (req: any) => 
          req.influencer_name !== null && 
          req.influencer_name !== undefined && // Added check for undefined
          req.influencer_profile_id !== null
      );
      setRequests(influencerRequests || []);
    }
  }, [verificationsData]);

  const handleConfirm = async () => {
    if (popup) {
      const id = popup.request.influencer_profile_id;
      const is_verified = popup.type === "approve";

      try {
        await verifyInfluencerMutation.mutateAsync({ id, status: is_verified });
        // Refetch handled by hook invalidation, but we might want to update local request state if needed immediately
        // Though hook invalidation is cleaner
      } catch (err) {
        console.log(err);
      }
      setPopup(null);
    }
  };

  // Helper to get status display text
  const getStatusDisplay = (status: string) => {
    if (!status) return "UNKNOWN";
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  // Filter requests based on search and status - FIXED with null check
  const filteredRequests = requests.filter(request => {
    // Add null/undefined check for influencer_name
    const matchesSearch = request.influencer_name 
      ? request.influencer_name.toLowerCase().includes(searchTerm.toLowerCase())
      : false; // If no name, don't include in search results
    
    const matchesStatus = statusFilter === "ALL" || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  // TanStack Table column definitions
  const columnHelper = createColumnHelper<InfluencerVerificationRequest>();

  const columns: ColumnDef<InfluencerVerificationRequest, any>[] = [
    columnHelper.accessor('influencer_name', {
      header: 'Influencer',
      cell: (info) => {
        const name = info.getValue();
        return <div className="font-medium">{name || 'Unknown Influencer'}</div>;
      },
    }),
    columnHelper.accessor('requested_at', {
      header: 'Requested At',
      cell: (info) => {
        const date = info.getValue();
        return (
          <div>
            {date ? new Date(date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }) : 'N/A'}
          </div>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const status = info.getValue();
        return (
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: status === "APPROVED" ? currentTheme.success :
                status === "REJECTED" ? currentTheme.error : currentTheme.warning,
              color: currentTheme.inverseText
            }}
          >
            {getStatusDisplay(status)}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: (props) => {
        const request = props.row.original;
        return (
          <div className="text-right text-sm font-medium flex justify-end items-center space-x-3">
            <Link
              to={`/dashboard/viewdocs/influencer/${request.influencer_id}`}
              className="transition-colors p-2 rounded-full"
              style={{ color: currentTheme.text }}
              title="View Documents"
            >
              <FaEye className="w-5 h-5" />
            </Link>
            <button
              className="p-2 rounded-full transition-colors"
              style={{
                color: request.status === "APPROVED" ? currentTheme.textSecondary : currentTheme.success
              }}
              onClick={() =>
                request.status !== "APPROVED" &&
                setPopup({ type: "approve", request })
              }
              disabled={request.status === "APPROVED"}
              title="Approve Request"
            >
              <FaCheckCircle className="w-5 h-5" />
            </button>
            <button
              className="p-2 rounded-full transition-colors"
              style={{
                color: request.status === "REJECTED" ? currentTheme.textSecondary : currentTheme.error
              }}
              onClick={() =>
                request.status !== "REJECTED" &&
                setPopup({ type: "reject", request })
              }
              disabled={request.status === "REJECTED"}
              title="Reject Request"
            >
              <FaTimesCircle className="w-5 h-5" />
            </button>
          </div>
        );
      },
    }),
  ];

  // TanStack Table instance
  const table = useReactTable({
    data: filteredRequests,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  // Get sorted rows, then paginate manually
  const sortedRows = table.getRowModel().rows;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  return (
    <div className="p-4 md:p-6" style={{ backgroundColor: currentTheme.background }}>
      <div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: currentTheme.text }}>Influencer Verification Requests</h1>
            <p className="mt-1" style={{ color: currentTheme.textSecondary }}>Manage influencer verification status</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search influencers..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent"
                style={{
                  borderColor: currentTheme.border,
                  backgroundColor: currentTheme.surface,
                  color: currentTheme.text
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-3 top-3" style={{ color: currentTheme.textSecondary }} />
            </div>

            <div className="relative w-full sm:w-40">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent appearance-none"
                style={{
                  borderColor: currentTheme.border,
                  backgroundColor: currentTheme.surface,
                  color: currentTheme.text
                }}
              >
                <option value="ALL">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <FaFilter className="absolute left-3 top-3" style={{ color: currentTheme.textSecondary }} />
            </div>
          </div>
        </div>

        <div className="rounded-xl shadow-md overflow-hidden" style={{ backgroundColor: currentTheme.surface }}>
          <div>
            <table className="min-w-full divide-y" style={{ borderColor: currentTheme.border }}>
              <thead style={{ backgroundColor: currentTheme.primary }} className="sticky top-0 z-10">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                        className={`px-6 py-1 text-left text-sm font-medium uppercase tracking-wider ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                        style={{ color: currentTheme.text }}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col leading-none" style={{ color: currentTheme.textMuted, fontSize: '10px' }}>
                              <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.3 }}>▲</span>
                              <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.3 }}>▼</span>
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row) => (
                    <tr
                      key={row.id}
                      onMouseEnter={() => setHoveredRow(row.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{ backgroundColor: hoveredRow === row.id ? (themeName === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)') : currentTheme.surface }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-1 whitespace-nowrap" style={{ color: currentTheme.text }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="text-lg" style={{ color: currentTheme.textSecondary }}>No verification requests found</div>
                      <p className="mt-2" style={{ color: currentTheme.textSecondary }}>
                        {searchTerm ? "Try a different search term" : "All requests are processed"}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredRequests.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-3">
                <span className="text-sm" style={{ color: currentTheme.textSecondary }}>Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                  className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: currentTheme.surface,
                    color: currentTheme.text,
                    borderColor: currentTheme.border
                  }}
                >
                  <option value={12}>12</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm" style={{ color: currentTheme.textMuted }}>
                  Showing {filteredRequests.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} requests
                </span>
              </div>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Popup */}
      {popup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="rounded-xl shadow-2xl max-w-md w-full mx-4 p-6" style={{ backgroundColor: currentTheme.surface }}>
            <h3 className="text-xl font-bold mb-3" style={{ color: currentTheme.text }}>
              {popup.type === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </h3>
            <div className="rounded-lg p-4 mb-5" style={{ backgroundColor: currentTheme.background }}>
              <div className="font-medium" style={{ color: currentTheme.text }}>{popup.request.influencer_name || 'Unknown Influencer'}</div>
              <div className="text-sm" style={{ color: currentTheme.textSecondary }}>
                Requested: {popup.request.requested_at ? new Date(popup.request.requested_at).toLocaleDateString() : 'N/A'}
              </div>
            </div>
            <p className="mb-5" style={{ color: currentTheme.textSecondary }}>
              Are you sure you want to {popup.type === "approve" ? "approve" : "reject"} this verification request?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 rounded-lg transition-colors border"
                style={{
                  color: currentTheme.text,
                  backgroundColor: currentTheme.surface,
                  borderColor: currentTheme.border
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.background}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.surface}
                onClick={() => setPopup(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white transition-colors"
                style={{
                  backgroundColor: popup.type === "approve" ? currentTheme.success : currentTheme.error,
                  color: currentTheme.inverseText
                }}
                onClick={handleConfirm}
              >
                {popup.type === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationRequestInfluencer;